import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Wrench, ArrowRight, Lock, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maintenance, setMaintenance] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/maintenance-status').then(res => {
      if (res.data.maintenance) setMaintenance(res.data.message);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Accesso effettuato con successo');
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: '#050816' }} data-testid="login-page">
      {/* Animated mesh background */}
      <div className="login-mesh-bg">
        <div className="login-blob login-blob-1" />
        <div className="login-blob login-blob-2" />
        <div className="login-blob login-blob-3" />
      </div>
      <div className="login-grain" />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="flex items-center gap-3 login-fade-1">
          <img src="/hostfix-logo-icon-small.png" alt="HostFix" className="w-9 h-9 rounded-lg" />
          <div>
            <p className="login-heading text-white font-bold text-base leading-tight">HostFix</p>
            <p className="text-[10px] text-white/40 uppercase tracking-widest">Hotel Maintenance IA</p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 login-fade-1 text-white/50 text-xs">
          <div className="login-live-dot" />
          <span className="font-medium">v3.0 · Tutti i sistemi operativi</span>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-200px)] px-4 pb-16">
        <div className="w-full max-w-md">
          {/* Headline */}
          <div className="text-center mb-8 sm:mb-10 login-fade-2">
            <h1 className="login-heading text-5xl sm:text-6xl font-black text-white leading-[0.95] mb-4">
              Bentornato.
            </h1>
            <p className="text-white/50 text-base">
              Accedi al cockpit della manutenzione.
            </p>
          </div>

          {/* Maintenance Banner */}
          {maintenance && (
            <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-start gap-3 login-fade-3" data-testid="maintenance-banner">
              <Wrench className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-300">App in manutenzione</p>
                <p className="text-xs text-amber-400/80 mt-0.5">{maintenance}</p>
              </div>
            </div>
          )}

          {/* Glass form card */}
          <div className="login-glass rounded-3xl p-6 sm:p-7 login-fade-3">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/15 text-red-300 rounded-xl text-sm border border-red-500/30" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="block text-[11px] font-semibold text-white/60 uppercase tracking-widest mb-2">Email</label>
                <input
                  type="email"
                  placeholder="nome@investhosp.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="login-input w-full px-4 py-3.5 rounded-xl text-[15px]"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-white/60 uppercase tracking-widest mb-2">Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="login-input w-full px-4 py-3.5 rounded-xl text-[15px]"
                  data-testid="password-input"
                />
              </div>

              <button
                type="submit"
                className="login-btn-primary w-full py-3.5 rounded-xl text-white font-semibold text-[15px] mt-2 flex items-center justify-center gap-2"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Accesso in corso...</span>
                  </>
                ) : (
                  <>
                    <span>Accedi al sistema</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Security trust signals */}
            <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between text-[11px] text-white/40">
              <span>Sessione protetta · TLS 1.3</span>
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3" />
                E2E Encrypted
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 login-fade-4">
            <p className="text-white/30 text-[11px]">PTK Solutions · 2026 — Powered by HostFix Engine v3.0</p>
          </div>
        </div>
      </div>

      {/* QR Code bottom-right */}
      <div className="hidden md:block fixed bottom-6 right-6 z-50 login-fade-4" data-testid="login-qr-code">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-3 flex items-center gap-3 border border-white/30">
          <QRCodeSVG
            value={window.location.origin}
            size={64}
            level="M"
            fgColor="#0F1E3D"
            includeMargin={false}
          />
          <div>
            <p className="text-[11px] font-bold text-[#0F1E3D] flex items-center gap-1">
              <Smartphone className="w-3 h-3" />
              Installa l'app
            </p>
            <p className="text-[10px] text-slate-500 leading-tight max-w-[120px]">Scansiona col telefono</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
